import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './FundingHistory.module.scss';
import { fetchFundingHistoryData } from '../../../../../../store/dashboard/overview/overview_apiHelper';
import { useParams } from 'react-router-dom';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../../../../app/hooks';
import { TabModule } from '../../../../../../utils/constants/commonConstants';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import FundingTable from './components/FundingTable';
import {
    DASHBOARD_TOTAL_TITLES,
    DEBENTURE_OR_CONVERTIBLE_INSTRUMENT,
    EMPTY_TABLE_MESSAGES,
    FUNDING_AMOUNT_HEADER_ID,
    FUNDING_TYPE_HEADER_ID,
    INSTRUMENT_FILTER_OPTIONS,
    INSTRUMENT_FILTERS,
    INSTRUMENT_HEADER_ID,
    InstrumentFilter,
    LAST_UPDATED_LABEL,
    NO_MCA_DATA_MESSAGE,
    PDF_LAST_UPDATED_LABEL,
    NUMBER_OF_SECURITIES_HEADER_ID,
    PREMIUM_NOMINAL_HEADER_ID,
} from './constants';
import CategorySelector from '../../../../../../common/CategorySelector/CategorySelector';
import AskAicaQuestionBadge from '../../../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import { DATE_FORMATS, formatDateByFormat, parseDayMonthYear } from '../../../../../../utils/dateUtils';

interface FundingHistoryData {
    fundingHistoryData: FundingHistoryRow[];
    debentureHistoryData: FundingHistoryRow[];
    lastUpdatedAt: string;
    taskTrackerData: any;
    totalFundingAmount: number;
    totalDebentureAmount: number;
    taskTrackerCreateDate?: string;
}

type FundingHistoryRow = {
    allotmentDate: string;
    fundingAmount: string;
    id: string;
    instrument: string;
    orgId: string;
    valuation: string;
    updatedAt: string;
    createdAt: string;
    numberOfSecurities?: string;
    nominalAmountPerSecurity?: string;
    premiumAmountPerSecurity?: string;
    fundingType?: string;
};

const HEADERS = {
    ALLOTMENT_DATE: { id: 'allotmentDate', label: 'Funding Date' },
    FUNDING_TYPE: { id: FUNDING_TYPE_HEADER_ID, label: 'Funding Type' },
    INSTRUMENT: { id: INSTRUMENT_HEADER_ID, label: 'Instruments' },
    FUNDING_AMOUNT: { id: FUNDING_AMOUNT_HEADER_ID, label: 'Funding Amount' },
    NUMBER_OF_SECURITIES: { id: NUMBER_OF_SECURITIES_HEADER_ID, label: 'No of Securities' },
    PREMIUM_NOMINAL: { id: PREMIUM_NOMINAL_HEADER_ID, label: 'Premium/Nominal Value' },
};

const DASHBOARD_HEADERS = [
    { ...HEADERS.ALLOTMENT_DATE, label: 'Date' },
    { ...HEADERS.INSTRUMENT, label: 'Instrument' },
    { ...HEADERS.FUNDING_TYPE, label: 'Allotment Type' },
    { ...HEADERS.FUNDING_AMOUNT, label: 'Amount' },
    HEADERS.NUMBER_OF_SECURITIES,
    HEADERS.PREMIUM_NOMINAL,
];

const PDF_HEADERS = [HEADERS.ALLOTMENT_DATE, HEADERS.FUNDING_TYPE, HEADERS.FUNDING_AMOUNT, HEADERS.INSTRUMENT];

const getFundingHistoryHeaders = (isPDFReport: boolean) => (isPDFReport ? PDF_HEADERS : DASHBOARD_HEADERS);

const AION_INGEST_FUNDING_HISTORY = 'AION_INGEST_FUNDING_HISTORY';

function FundingHistory({ isPDFReport = false }) {
    const MAX_POLLS = 10;
    const POLLING_TIME_INTERVAL_MS = 20000;
    const params = useParams();

    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);

    const [loading, setLoading] = useState(true);
    const [fundingHistory, setFundingHistory] = useState<null | FundingHistoryData>(null);
    const [taskTrackerStatus, setTaskTrackerStatus] = useState(null);
    const [instrumentFilter, setInstrumentFilter] = useState<InstrumentFilter>(INSTRUMENT_FILTERS.ALL);

    const ingestingData = taskTrackerStatus === 'TODO' || taskTrackerStatus === 'PROCESSING' || taskTrackerStatus === 'NOT_AVAILABLE';
    const showFundingData = !ingestingData && !!fundingHistory?.fundingHistoryData?.length;
    const showDebentureData = !ingestingData && !!fundingHistory?.debentureHistoryData?.length;

    const pollingRef = useRef<any>(null);
    const pollingCountRef = useRef(0);

    useEffect(() => {
        fetchData();

        return () => {
            pollingRef.current && clearInterval(pollingRef.current);
        };
    }, []);

    useEffect(() => {
        if (modulePollingList.includes(TabModule.RELATED_PARTY) || modulePollingList.includes(TabModule.MCA_FILING)) {
            fetchData();
        }
    }, [modulePollingList]);

    const fetchData = (isPolling = false) => {
        if (!isPolling) setLoading(true);
        else pollingCountRef.current += 1;
        fetchFundingHistoryData({
            orgId: params.id as string,
            assesseeOrgId: params.id as string,
            taskStepsList: [AION_INGEST_FUNDING_HISTORY],
        })
            .then((res) => {
                const taskStatus = res?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status;
                setFundingHistory({
                    fundingHistoryData: res?.fundingHistoryData || [],
                    debentureHistoryData: res?.debentureHistoryData || [],
                    lastUpdatedAt: res?.lastUpdatedDate?.[0]?.lastUpdatedAt || '',
                    taskTrackerData: res?.taskTrackerData || {},
                    totalFundingAmount: res?.totalFundingAmount || 0,
                    totalDebentureAmount: res?.totalDebentureAmount || 0,
                    taskTrackerCreateDate: res?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.dateCreated || '',
                });
                setTaskTrackerStatus(res?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status || 'NOT_AVAILABLE');

                // Start polling if: status is TODO/PROCESSING OR taskTrackerData is missing/undefined
                const shouldStartPolling =
                    ['TODO', 'PROCESSING'].includes(taskStatus) || !res?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY];

                if (shouldStartPolling && !pollingRef.current) {
                    pollingRef.current = setInterval(() => {
                        fetchData(true);
                    }, POLLING_TIME_INTERVAL_MS);
                } else if ((['COMPLETE', 'FAILED'].includes(taskStatus) && pollingRef.current) || pollingCountRef.current > MAX_POLLS) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                }
            })
            .catch((err) => {
                console.error('Error in fetch funding history', err);
            })
            .finally(() => setLoading(false));
    };

    // if (['TODO', 'PROCESSING', 'NOT_AVAILABLE', null].includes(taskTrackerStatus)) return <></>;

    const lastUpdatedLabel = isPDFReport ? PDF_LAST_UPDATED_LABEL : LAST_UPDATED_LABEL;

    const getHeaderContent = () => {
        const lastUpdatedAt = fundingHistory?.lastUpdatedAt;
        const taskTrackerStatus = fundingHistory?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status;
        const dateCreated = fundingHistory?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.dateCreated;

        // CASE 1: Use lastUpdatedAt if available
        if (lastUpdatedAt && dayjs(lastUpdatedAt).isValid()) {
            return (
                <div className={styles.lastUpdated}>
                    {lastUpdatedLabel} {dayjs(lastUpdatedAt).format('DD MMM YYYY')}
                </div>
            );
        }

        // CASE 3: Don't show date if status is FAILED
        if (taskTrackerStatus === 'FAILED') {
            return <div className={styles.statusMessage}>Not Able to Fetch</div>;
        }

        // CASE 2: Use dateCreated if lastUpdatedAt is null but status is COMPLETE
        if (!lastUpdatedAt && taskTrackerStatus === 'COMPLETE' && dateCreated) {
            return (
                <div className={styles.lastUpdated}>
                    {lastUpdatedLabel} {dayjs(dateCreated).format('DD MMM YYYY')}
                </div>
            );
        }

        // Default: No date shown
        return null;
    };

    const shouldShowTable = () => {
        const taskTrackerStatus = fundingHistory?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status;
        return taskTrackerStatus !== 'FAILED' || showFundingData || showDebentureData;
    };

    const dashboardRows = filterRowsByInstrument(fundingHistory, instrumentFilter);

    const rootClassName = clsx(styles.fundingHistory, !isPDFReport && styles.dashboardCard);

    const isEmpty = !ingestingData && !showFundingData && !showDebentureData;
    if (isEmpty && !isPDFReport) {
        return (
            <div className={rootClassName}>
                <div className={styles.emptyHeader}>
                    <h2 className={styles.heading}>Funding History</h2>
                    <div className={styles.emptyRight}>
                        <span className={styles.noMcaData}>{NO_MCA_DATA_MESSAGE}</span>
                        {fundingHistory?.taskTrackerCreateDate && (
                            <span className={styles.emptyDate}>
                                {lastUpdatedLabel}{' '}
                                {formatDateByFormat(fundingHistory.taskTrackerCreateDate, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const render = (
        <div className={rootClassName}>
            <div className={clsx(styles.header, { [styles.headerDashboard]: !isPDFReport })}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.heading}>Funding History</h2>
                    {!isPDFReport && !ingestingData && (
                        <div className={styles.instrumentFilter}>
                            <CategorySelector
                                categoryOptions={INSTRUMENT_FILTER_OPTIONS}
                                selectedCategory={instrumentFilter}
                                changeSelectedTab={(filter) => setInstrumentFilter(filter as InstrumentFilter)}
                                equalWidth
                            />
                        </div>
                    )}
                </div>
                <div className={styles.headerRight}>
                    {ingestingData ? (
                        <div className={styles.loaderMessage}>
                            <LoadingSpinner color="#6021b3" height="15px" />
                            <span>Fetching Data</span>
                        </div>
                    ) : (
                        getHeaderContent()
                    )}
                </div>
            </div>

            {!ingestingData && shouldShowTable() && (
                <div className={styles.content}>
                    <div className={styles.contentBody}>
                        <FundingTable
                            headers={getFundingHistoryHeaders(isPDFReport)}
                            fundingData={isPDFReport ? fundingHistory?.fundingHistoryData || [] : dashboardRows}
                            debentureData={isPDFReport ? fundingHistory?.debentureHistoryData || [] : []}
                            totalFundingAmount={isPDFReport ? fundingHistory?.totalFundingAmount || 0 : sumFundingAmountCr(dashboardRows)}
                            totalDebentureAmount={fundingHistory?.totalDebentureAmount || 0}
                            isPDFReport={isPDFReport}
                            showFundingData={isPDFReport ? showFundingData : dashboardRows.length > 0}
                            showDebentureData={isPDFReport && showDebentureData}
                            taskTrackerStatus={fundingHistory?.taskTrackerData?.[AION_INGEST_FUNDING_HISTORY]?.status || ''}
                            emptyMessage={EMPTY_TABLE_MESSAGES[instrumentFilter]}
                            fundingTotalTitle={isPDFReport ? undefined : DASHBOARD_TOTAL_TITLES[instrumentFilter]}
                        />
                    </div>
                </div>
            )}
        </div>
    );

    if (isPDFReport)
        return reportSectionsToShow.includes(REPORT_SECTION_NAMES.FUNDING_HISTORY) && fundingHistory?.fundingHistoryData?.length ? (
            <A4Page sectionName={REPORT_SECTION_NAMES.FUNDING_HISTORY}>{render}</A4Page>
        ) : (
            <></>
        );
    else return render;
}

export default FundingHistory;

function filterRowsByInstrument(fundingHistory: FundingHistoryData | null, filter: InstrumentFilter): FundingHistoryRow[] {
    const debentureRows = fundingHistory?.debentureHistoryData || [];
    const rows = sortByAllotmentDateDesc([...(fundingHistory?.fundingHistoryData || []), ...debentureRows]);
    if (filter === INSTRUMENT_FILTERS.ALL) return rows;
    const wantsDebentures = filter === INSTRUMENT_FILTERS.DEBENTURES_CONVERTIBLE;
    return rows.filter(
        (row) => (debentureRows.includes(row) || DEBENTURE_OR_CONVERTIBLE_INSTRUMENT.test(row.instrument ?? '')) === wantsDebentures,
    );
}

function sortByAllotmentDateDesc(rows: FundingHistoryRow[]): FundingHistoryRow[] {
    const allotmentTime = (row: FundingHistoryRow) => parseDayMonthYear(row.allotmentDate)?.valueOf() ?? null;
    return rows
        .map((row) => ({ row, time: allotmentTime(row) }))
        .sort((a, b) => {
            if (a.time === null || b.time === null) return Number(a.time === null) - Number(b.time === null);
            return b.time - a.time;
        })
        .map(({ row }) => row);
}

/** Sum of the rows' Crore amounts ("2.08 Cr"), the same way polus totals them; unreadable amounts count as 0. */
function sumFundingAmountCr(rows: FundingHistoryRow[]): number {
    return rows.reduce((total, row) => total + (parseFloat(row.fundingAmount?.replace(/,/g, '') ?? '') || 0), 0);
}
